import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  isLoading: false,
  queryOutput: [] as Record<string, any>[],
  outputMetadata: {} as Record<string, any>,
  isPerformanceApiCalled: false,
  isComparisonLoading: false,
  comparisonQueryOutput: [] as Record<string, any>[],

  //Budget states
  budgetDataOfBizDevelopment: [] as Record<string, any>[],
  budgetMetaDataOfBizDevelopment: [] as Record<string, any>[],
  isFetchedBudgetData: false as boolean,
  isBudgetOverused: false as boolean, // budget overuse
  brandNameForBudget: "" as string, // tracks which brand was last fetched for comparison

  comparisonFetchedFor: null as string | null, // tracks which timeframe was last fetched for comparison
  performanceBrandSearch: "" as string,
  // Overview state
  isOverviewAccountSummaryLoading: false,
  overviewAccountSummaryOutput: [] as Record<string, any>[],
  overviewAccountSummaryLoadedRequestKey: null as string | null,
  overviewAccountSummaryActiveRequestKey: null as string | null,
  isOverviewConversionsLoading: false,
  overviewConversionsOutput: [] as Record<string, any>[],
  overviewConversionsLoadedRequestKey: null as string | null,
  overviewConversionsDataSet: "action",
  overviewConversionsFilters: [] as { key: string; value: string }[],
  overviewConversionsFilterOptions: {
    trafficSource: [] as Record<string, any>[],
    country: [] as Record<string, any>[],
    campaign: [] as Record<string, any>[],
    adgroup: [] as Record<string, any>[],
    keyword: [] as Record<string, any>[],
  },
  isOverviewConversionsFilterLoading: false,
  isOverviewConversionsNotesLoading: false,
  overviewConversionsNotes: [] as Record<string, any>[],
  overviewConversionsNotesLoadedRequestKey: null as string | null,
  overviewConversionsBaseFilterOptionsLoaded: false,

  // Be Aware state
  isBeAwareFraudPlayersLoading: false,
  beAwareFraudPlayersOutput: [] as Record<string, any>[],
  beAwareFraudPlayersLoadedRequestKey: null as string | null,
  isBeAwareNegativeBalanceLoading: false,
  beAwareNegativeBalanceOutput: [] as Record<string, any>[],
  beAwareNegativeBalanceLoadedRequestKey: null as string | null,
  isBeAwareConversionsUploadLoading: false,
  beAwareConversionsUploadOutput: [] as Record<string, any>[],
  beAwareConversionsUploadLoadedRequestKey: null as string | null,
};

const bizDevelopmentDashboardSlice = createSlice({
  name: "bizDevelopmentDashboard",
  initialState,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setQueryOutput: (state, action) => {
      state.queryOutput = action.payload;
    },
    setOutputMetadata: (state, action) => {
      state.outputMetadata = action.payload;
    },
    setIsPerformanceApiCalled: (state, action) => {
      state.isPerformanceApiCalled = action.payload;
    },
    setIsComparisonLoading: (state, action) => {
      state.isComparisonLoading = action.payload;
    },
    setComparisonQueryOutput: (state, action) => {
      state.comparisonQueryOutput = action.payload;
    },

    //Budget reducers
    setBudgetDataOfBizDevelopment: (state, action) => {
      state.budgetDataOfBizDevelopment = action.payload;
    },
    setBudgetMetaDataOfBizDevelopment: (state, action) => {
      state.budgetMetaDataOfBizDevelopment = action.payload;
    },
    setIsFetchedBudgetData: (state, action) => {
      state.isFetchedBudgetData = action.payload;
    },
    setIsBudgetOverused: (state, action) => {
      state.isBudgetOverused = action.payload;
    },
    setBrandNameForBudget: (state, action) => {
      state.brandNameForBudget = action.payload;
    },
    setComparisonFetchedFor: (state, action) => {
      state.comparisonFetchedFor = action.payload;
    },
    setPerformanceBrandSearch: (state, action) => {
      state.performanceBrandSearch = action.payload;
    },
    setIsOverviewAccountSummaryLoading: (state, action) => {
      state.isOverviewAccountSummaryLoading = action.payload;
    },
    setOverviewAccountSummaryOutput: (state, action) => {
      state.overviewAccountSummaryOutput = action.payload;
    },
    setOverviewAccountSummaryLoadedRequestKey: (state, action) => {
      state.overviewAccountSummaryLoadedRequestKey = action.payload;
    },
    setOverviewAccountSummaryActiveRequestKey: (state, action) => {
      state.overviewAccountSummaryActiveRequestKey = action.payload;
    },
    setIsOverviewConversionsLoading: (state, action) => {
      state.isOverviewConversionsLoading = action.payload;
    },
    setOverviewConversionsOutput: (state, action) => {
      state.overviewConversionsOutput = action.payload;
    },
    setOverviewConversionsLoadedRequestKey: (state, action) => {
      state.overviewConversionsLoadedRequestKey = action.payload;
    },
    setOverviewConversionsDataSet: (state, action) => {
      state.overviewConversionsDataSet = action.payload;
    },
    setOverviewConversionsFilter: (state: any, action) => {
      const existingIndex = state.overviewConversionsFilters.findIndex(
        (filter: any) => filter.key === action.payload.key,
      );
      if (existingIndex >= 0) {
        state.overviewConversionsFilters[existingIndex] = action.payload;
      } else {
        state.overviewConversionsFilters.push(action.payload);
      }
    },
    removeOverviewConversionsFilter: (state, action) => {
      state.overviewConversionsFilters =
        state.overviewConversionsFilters.filter(
          (filter) => filter.key !== action.payload,
        );
    },
    clearOverviewConversionsFilters: (state) => {
      state.overviewConversionsFilters = [];
    },
    setOverviewConversionsFilterOptions: (state: any, action) => {
      const { key, data } = action.payload;
      state.overviewConversionsFilterOptions[key] = data;
    },
    clearOverviewConversionsFilterOptions: (state) => {
      state.overviewConversionsFilterOptions = {
        trafficSource: [],
        country: [],
        campaign: [],
        adgroup: [],
        keyword: [],
      };
      state.overviewConversionsBaseFilterOptionsLoaded = false;
    },
    setIsOverviewConversionsFilterLoading: (state, action) => {
      state.isOverviewConversionsFilterLoading = action.payload;
    },
    setIsOverviewConversionsNotesLoading: (state, action) => {
      state.isOverviewConversionsNotesLoading = action.payload;
    },
    setOverviewConversionsNotes: (state, action) => {
      state.overviewConversionsNotes = action.payload;
    },
    setOverviewConversionsNotesLoadedRequestKey: (state, action) => {
      state.overviewConversionsNotesLoadedRequestKey = action.payload;
    },
    setOverviewConversionsBaseFilterOptionsLoaded: (state, action) => {
      state.overviewConversionsBaseFilterOptionsLoaded = action.payload;
    },

    //Be Aware reducers
    setIsBeAwareFraudPlayersLoading: (state, action) => {
      state.isBeAwareFraudPlayersLoading = action.payload;
    },
    setBeAwareFraudPlayersOutput: (state, action) => {
      state.beAwareFraudPlayersOutput = action.payload;
    },
    setBeAwareFraudPlayersLoadedRequestKey: (state, action) => {
      state.beAwareFraudPlayersLoadedRequestKey = action.payload;
    },
    setIsBeAwareNegativeBalanceLoading: (state, action) => {
      state.isBeAwareNegativeBalanceLoading = action.payload;
    },
    setBeAwareNegativeBalanceOutput: (state, action) => {
      state.beAwareNegativeBalanceOutput = action.payload;
    },
    setBeAwareNegativeBalanceLoadedRequestKey: (state, action) => {
      state.beAwareNegativeBalanceLoadedRequestKey = action.payload;
    },
    setIsBeAwareConversionsUploadLoading: (state, action) => {
      state.isBeAwareConversionsUploadLoading = action.payload;
    },
    setBeAwareConversionsUploadOutput: (state, action) => {
      state.beAwareConversionsUploadOutput = action.payload;
    },
    setBeAwareConversionsUploadLoadedRequestKey: (state, action) => {
      state.beAwareConversionsUploadLoadedRequestKey = action.payload;
    },
    resetBizDevelopmentDashboardState: () => initialState,
  },
});

export const {
  setIsLoading,
  setQueryOutput,
  setOutputMetadata,
  setIsPerformanceApiCalled,
  setIsComparisonLoading,
  setComparisonQueryOutput,
  //Budget actions
  setBudgetDataOfBizDevelopment,
  setBudgetMetaDataOfBizDevelopment,
  setIsFetchedBudgetData,
  setIsBudgetOverused,
  setBrandNameForBudget,
  setComparisonFetchedFor,
  setPerformanceBrandSearch,
  setIsOverviewAccountSummaryLoading,
  setOverviewAccountSummaryOutput,
  setOverviewAccountSummaryLoadedRequestKey,
  setOverviewAccountSummaryActiveRequestKey,
  setIsOverviewConversionsLoading,
  setOverviewConversionsOutput,
  setOverviewConversionsLoadedRequestKey,
  setOverviewConversionsDataSet,
  setOverviewConversionsFilter,
  removeOverviewConversionsFilter,
  clearOverviewConversionsFilters,
  setOverviewConversionsFilterOptions,
  clearOverviewConversionsFilterOptions,
  setIsOverviewConversionsFilterLoading,
  setIsOverviewConversionsNotesLoading,
  setOverviewConversionsNotes,
  setOverviewConversionsNotesLoadedRequestKey,
  setOverviewConversionsBaseFilterOptionsLoaded,
  //Be Aware actions
  setIsBeAwareFraudPlayersLoading,
  setBeAwareFraudPlayersOutput,
  setBeAwareFraudPlayersLoadedRequestKey,
  setIsBeAwareNegativeBalanceLoading,
  setBeAwareNegativeBalanceOutput,
  setBeAwareNegativeBalanceLoadedRequestKey,
  setIsBeAwareConversionsUploadLoading,
  setBeAwareConversionsUploadOutput,
  setBeAwareConversionsUploadLoadedRequestKey,
  resetBizDevelopmentDashboardState,
} = bizDevelopmentDashboardSlice.actions;

export const selectBizDashboardLoading = (state: any) =>
  state.bizDevelopmentDashboard.isLoading;
export const selectBizQueryOutput = (state: any) =>
  state.bizDevelopmentDashboard.queryOutput;
export const selectBizOutputMetadata = (state: any) =>
  state.bizDevelopmentDashboard.outputMetadata;
export const selectBizIsPerformanceApiCalled = (state: any) =>
  state.bizDevelopmentDashboard.isPerformanceApiCalled;
export const selectBizComparisonLoading = (state: any) =>
  state.bizDevelopmentDashboard.isComparisonLoading;
export const selectBizComparisonQueryOutput = (state: any) =>
  state.bizDevelopmentDashboard.comparisonQueryOutput;
export const selectBudgetDataOfBizDevelopment = (state: any) =>
  state.bizDevelopmentDashboard.budgetDataOfBizDevelopment;
export const selectBudgetMetaDataOfBizDevelopment = (state: any) =>
  state.bizDevelopmentDashboard.budgetMetaDataOfBizDevelopment;
export const selectBizComparisonFetchedFor = (state: any) =>
  state.bizDevelopmentDashboard.comparisonFetchedFor;
export const selectBizPerformanceBrandSearch = (state: any) =>
  state.bizDevelopmentDashboard.performanceBrandSearch;
export const selectBizOverviewAccountSummaryLoading = (state: any) =>
  state.bizDevelopmentDashboard.isOverviewAccountSummaryLoading;
export const selectBizOverviewAccountSummaryOutput = (state: any) =>
  state.bizDevelopmentDashboard.overviewAccountSummaryOutput;
export const selectBizOverviewAccountSummaryLoadedRequestKey = (state: any) =>
  state.bizDevelopmentDashboard.overviewAccountSummaryLoadedRequestKey;
export const selectBizOverviewAccountSummaryActiveRequestKey = (state: any) =>
  state.bizDevelopmentDashboard.overviewAccountSummaryActiveRequestKey;
export const selectBizOverviewConversionsLoading = (state: any) =>
  state.bizDevelopmentDashboard.isOverviewConversionsLoading;
export const selectBizOverviewConversionsOutput = (state: any) =>
  state.bizDevelopmentDashboard.overviewConversionsOutput;
export const selectBizOverviewConversionsLoadedRequestKey = (state: any) =>
  state.bizDevelopmentDashboard.overviewConversionsLoadedRequestKey;
export const selectBizOverviewConversionsDataSet = (state: any) =>
  state.bizDevelopmentDashboard.overviewConversionsDataSet;
export const selectBizOverviewConversionsFilters = (state: any) =>
  state.bizDevelopmentDashboard.overviewConversionsFilters;
export const selectBizOverviewConversionsFilterOptions = (state: any) =>
  state.bizDevelopmentDashboard.overviewConversionsFilterOptions;
export const selectBizOverviewConversionsFilterLoading = (state: any) =>
  state.bizDevelopmentDashboard.isOverviewConversionsFilterLoading;
export const selectBizOverviewConversionsNotesLoading = (state: any) =>
  state.bizDevelopmentDashboard.isOverviewConversionsNotesLoading;
export const selectBizOverviewConversionsNotes = (state: any) =>
  state.bizDevelopmentDashboard.overviewConversionsNotes;
export const selectBizOverviewConversionsNotesLoadedRequestKey = (state: any) =>
  state.bizDevelopmentDashboard.overviewConversionsNotesLoadedRequestKey;
export const selectBizOverviewConversionsBaseFilterOptionsLoaded = (
  state: any,
) => state.bizDevelopmentDashboard.overviewConversionsBaseFilterOptionsLoaded;

// Be Aware selectors
export const selectBizBeAwareFraudPlayersLoading = (state: any) =>
  state.bizDevelopmentDashboard.isBeAwareFraudPlayersLoading;
export const selectBizBeAwareFraudPlayersOutput = (state: any) =>
  state.bizDevelopmentDashboard.beAwareFraudPlayersOutput;
export const selectBizBeAwareFraudPlayersLoadedRequestKey = (state: any) =>
  state.bizDevelopmentDashboard.beAwareFraudPlayersLoadedRequestKey;
export const selectBizBeAwareNegativeBalanceLoading = (state: any) =>
  state.bizDevelopmentDashboard.isBeAwareNegativeBalanceLoading;
export const selectBizBeAwareNegativeBalanceOutput = (state: any) =>
  state.bizDevelopmentDashboard.beAwareNegativeBalanceOutput;
export const selectBizBeAwareNegativeBalanceLoadedRequestKey = (state: any) =>
  state.bizDevelopmentDashboard.beAwareNegativeBalanceLoadedRequestKey;
export const selectBizBeAwareConversionsUploadLoading = (state: any) =>
  state.bizDevelopmentDashboard.isBeAwareConversionsUploadLoading;
export const selectBizBeAwareConversionsUploadOutput = (state: any) =>
  state.bizDevelopmentDashboard.beAwareConversionsUploadOutput;
export const selectBizBeAwareConversionsUploadLoadedRequestKey = (state: any) =>
  state.bizDevelopmentDashboard.beAwareConversionsUploadLoadedRequestKey;

export default bizDevelopmentDashboardSlice.reducer;
